import { FC, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { CustomCellRendererProps } from "ag-grid-react";
import { AutomationStatusChip } from "../../../common/components/grid/AutomationStatusChip";
import { getOffHoursNextRun } from "../../utils/helpers/getOffHoursNextRun";
import { OffHoursSchedule } from "@/services/cloudchipr.api";
import {
  dateToUtcByTimeZone,
  formatDate,
} from "@/utils/helpers/date-time/datetime-format";
import { TooltipText } from "@/components/common/TooltipText";

export const OffHoursDataGridNextRunGridCell: FC<
  CustomCellRendererProps<OffHoursSchedule>
> = ({ data }) => {
  const { status, time_zone: timeZone, metadata } = data ?? {};

  const { nextRun, type } = useMemo(
    () =>
      getOffHoursNextRun(
        metadata?.next_stop_run_at ?? "",
        metadata?.next_start_run_at ?? "",
        status ?? "active",
      ),
    [metadata?.next_stop_run_at, metadata?.next_start_run_at, status],
  );

  if (!data) {
    return "-";
  }

  if (status === "inactive") {
    return <AutomationStatusChip status={status} />;
  }

  if (!nextRun) {
    return (
      <Typography variant="body2" pl={1}>
        Never
      </Typography>
    );
  }

  return (
    <Stack pl={1} spacing={0.25}>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {type && `${type.toUpperCase()} | `}
        {formatDate(nextRun, {
          type: "date",
        })}
      </Typography>

      <TooltipText
        tooltipTitle={`${formatDate(
          dateToUtcByTimeZone(nextRun, timeZone) || nextRun,
          {
            type: "dateTime",
          },
        )} in UTC time`}
        variant="body2"
        color="text.secondary"
        width="fit-content"
      >
        {formatDate(nextRun, {
          type: "time",
        })}
      </TooltipText>
    </Stack>
  );
};
